import { ConfigFn } from '@config/types'

export type FeatureFlagsEntry = {
  description: string
  values: { name: string | number | true; description?: string }[]
  disableInGrowthbook?: true
}

/*
 * When setting global window variables to check against in an optimize A/B test,
 * use the windowKey below as the window property. Then set your variantKey with its
 * value(s) and copy it in the array below. The ABTestManager will update, when the
 * Optimize script tries to set the property, the relevant field of ReactQuery.
 */
const config = (({ publication }) =>
  ({
    dashboardPath:
      publication === 'romandie'
        ? '/fr/test_section/dashboard/'
        : '/Test_Section/dashboard/',
    windowKey: 'blick_abTest',
    localStorageUpdateKey: 'localStorageUpdateKey',
    featureFlagsEntries: {
      showPianoRelatedLogs: {
        description: 'Show console logs for Piano. (Used by developers only)',
        values: [{ name: 'show', description: 'Show Logs' }],
        disableInGrowthbook: true,
      },
      pianoDebugMode: {
        description: 'Enable Piano debug mode',
        values: [
          {
            name: 'active',
            description:
              'Enable Piano verbose logging - Note: requires reload of the page. (Used by developers only)',
          },
        ],
        disableInGrowthbook: true,
      },
      showReactQueryDevtoolsOnProd: {
        description:
          'Show React Query Devtools on production builds of React. (Used by developers only)',
        values: [{ name: 'show', description: 'Show Devtools on Production.' }],
        disableInGrowthbook: true,
      },
      showThemeSwitcher: {
        description:
          'Show the Theme Switcher (auto | force light | force dark)',
        values: [{ name: 'show', description: 'Show the Theme Switcher.' }],
        disableInGrowthbook: true,
      },
      showCMPLogs: {
        description: 'Show console logs for CMP. (Used by developers only)',
        values: [{ name: 'show', description: 'Show Logs' }],
        disableInGrowthbook: true,
      },
      showAdLogs: {
        description: 'Show console logs for Ads. (Used by developers only)',
        values: [{ name: 'show', description: 'Show Logs' }],
        disableInGrowthbook: true,
      },
      showCUI: {
        description: 'Show the Chatbot UI',
        values: [{ name: 'show', description: 'Show CUI' }],
        disableInGrowthbook: true,
      },
      showNewArticleSummary: {
        description: 'Show new style for article summary widget',
        values: [{ name: 'show', description: 'Show new article summary' }],
      },
      showVideoPlayerLogs: {
        description:
          'Show console logs for Video Player. (Used by developers only)',
        values: [{ name: 'show', description: 'Show Logs' }],
        disableInGrowthbook: true,
      },
      showOneSignalLogs: {
        description:
          'Show console logs for OneSignal. (Used by developers only)',
        values: [{ name: 'show', description: 'Show Logs' }],
        disableInGrowthbook: true,
      },
      enableGrowthBookDevMode: {
        description: 'Enable GrowthBook DevMode. (Used by developers only)',
        values: [{ name: 'enable', description: 'Enable GrowthBook DevMode' }],
        disableInGrowthbook: true,
      },
      showTrackingDebugView: {
        description: 'Show Tracking debug view',
        values: [
          {
            name: 'show',
            description:
              'Show Tracking debug view (Reload is needed after activation)',
          },
        ],
        disableInGrowthbook: true,
      },
      containerPiano1: {
        description:
          'First Piano container: it can be either a widget or a Piano template',
        values: [],
        disableInGrowthbook: true,
      },
      containerPiano2: {
        description:
          'Second Piano container: it can be either a widget or a Piano template',
        values: [],
        disableInGrowthbook: true,
      },
      showGeolocation: {
        description:
          'Show an indicator that tells whether the user is in Switzerland or not ( Used to test geoblocked video)',
        values: [{ name: 'show', description: 'Show geolocation' }],
        disableInGrowthbook: true,
      },
      'ai-search-badge': {
        description: 'Show NEU badge for AI search button',
        values: [{ name: 'show', description: 'Show AI search badge' }],
      },
      onboardingStepsOrder: {
        description:
          'Order of the onboarding steps for newly subscribed B+ users',
        values: [
          {
            name: 'welcome,app',
            description: '1. Welcome 2. App',
          },
          {
            name: 'welcome,app,newsletter',
            description: '1. Welcome 2. App 3. Newsletter',
          },
          {
            name: 'welcome,app,highlights',
            description: '1. Welcome 2. App 3. Highlights',
          },
          {
            name: 'welcome,app,newsletter,highlights',
            description: '1. Welcome 2. App 3. Newsletter 4. Highlights',
          },
          {
            name: 'welcome,app,highlights,newsletter',
            description: '1. Welcome 2. App 3. Highlights 4. Newsletter',
          },
          {
            name: 'welcome,newsletter,app,highlights',
            description: '1. Welcome 2. Newsletter 3. App 4. Highlights',
          },
          {
            name: 'welcome,newsletter,highlights,app',
            description: '1. Welcome 2. Newsletter 3. Highlights 4. App',
          },
          {
            name: 'welcome,highlights,app,newsletter',
            description: '1. Welcome 2. Highlights 3. App  4. Newsletter',
          },
          {
            name: 'welcome,highlights,newsletter,app',
            description: '1. Welcome 2. Highlights 3. Newsletter  4. App',
          },
        ],
        disableInGrowthbook: true,
      },
      enableDelayedDeBoostingEvent: {
        description:
          'Enable delayed de-bosting events for Aureus recommendations',
        values: [
          {
            description:
              'Experimentation segment: De-boosting events will be triggered after 5s',
            name: 'test',
          },
          {
            description:
              'Experimentation segment: De-boosting events will be triggered immediately',
            name: 'control',
          },
          {
            description: 'No experiment running',
            name: 'reference',
          },
        ],
      },
      combinationVideoWidget: {
        description: 'Show combination video widget',
        values: [
          {
            description: 'Experimentation segment: Combination video will show',
            name: 'test',
          },
          {
            description:
              'Experimentation segment: Combination video will not show',
            name: 'control',
          },
        ],
      },
      enableBPlusFlagOnHero: {
        description: 'Toggle B+ flag on the first teaser in the hero element',
        values: [
          {
            description:
              'Hides B+ flag on the first teaser in the hero element',
            name: 'hidePlusFlagOnHero',
          },
          {
            description:
              'Shows B+ flag on the first teaser in the hero element',
            name: 'showPlusFlagOnHero',
          },
        ],
      },
    } as const satisfies Record<string, FeatureFlagsEntry>,
  }) as const) satisfies ConfigFn

export default config
